import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Typography, Button, TextField, MenuItem, Select,
  FormControl, InputLabel, Paper, Grid, CircularProgress, Alert, Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import api from "../../../services/api";

const CONDITIONS = [
  { value: "baik",          label: "Baik" },
  { value: "cukup_baik",   label: "Cukup Baik" },
  { value: "rusak_ringan",  label: "Rusak Ringan" },
  { value: "rusak_berat",   label: "Rusak Berat" },
];

const UNIT_OPTIONS = [
  { value: "Pcs",  label: "Pcs" },
  { value: "Unit", label: "Unit" },
  { value: "Set",  label: "Set" },
  { value: "Pair", label: "Pair (Pasang)" },
];

const STATUSES = [
  { value: "aktif",           label: "Aktif" },
  { value: "tidak_aktif",     label: "Tidak Aktif" },
  { value: "dipinjam",        label: "Dipinjam" },
  { value: "dalam_perbaikan", label: "Dalam Perbaikan" },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" },
  "& .MuiInputLabel-root":    { fontSize: "0.875rem" },
};

const toDatetimeLocal = (val) => {
  if (!val) return "";
  try { return new Date(val).toISOString().slice(0, 16); }
  catch { return ""; }
};

export default function ItemCreateEdit() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const isEdit   = Boolean(id);

  const [rooms,       setRooms]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [loadingInit, setLoadingInit] = useState(true);
  const [error,       setError]       = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMsg,  setSuccessMsg]  = useState("");
  const [form, setForm] = useState({
    name: "", serial_number: "", category: "",
    specification: "", quantity: 1, unit: "Unit",
    description: "", condition: "baik", status: "aktif",
    purchase_date: "", purchase_price: "", room_id: "", note: "",
  });

  useEffect(() => {
    const init = async () => {
      setLoadingInit(true);
      try {
        const roomsRes  = await api.get("/rooms");
        const roomsData = Array.isArray(roomsRes.data) ? roomsRes.data
          : Array.isArray(roomsRes.data?.data?.data) ? roomsRes.data.data.data
          : roomsRes.data?.data ?? [];
        setRooms(roomsData);

        if (isEdit) {
          const itemRes = await api.get(`/items/${id}`);
          const item    = itemRes.data.data;
          setForm({
            name:           item.name          || "",
            serial_number:  item.serial_number || "",
            category:       item.category      || "",
            specification:  item.specification || "",
            quantity:       item.quantity      ?? 1,
            unit:           item.unit          || "Unit",
            description:    item.description   || "",
            condition:      item.condition     || "baik",
            status:         item.status        || "aktif",
            purchase_date:  toDatetimeLocal(item.purchase_date),
            purchase_price: item.purchase_price ? String(item.purchase_price) : "",
            room_id:        item.room_id       ? String(item.room_id) : "",
            note: "",
          });
        } else {
          // No auto-generate serial number, biarkan kosong agar bisa diisi manual
          setForm((prev) => ({ ...prev, serial_number: "", unit: "Unit" }));
        }
      } catch {
        setError("Gagal memuat data.");
      } finally {
        setLoadingInit(false);
      }
    };
    init();
  }, [id, isEdit]);

  const [generatingCode, setGeneratingCode] = useState(false);

  const handleGenerateCode = async () => {
    if (!form.name.trim()) {
      setFieldErrors((prev) => ({ ...prev, name: "Isi Nama Barang terlebih dahulu untuk generate kode otomatis." }));
      setError("Isi Nama Barang terlebih dahulu untuk generate kode otomatis.");
      return;
    }
    try {
      setGeneratingCode(true);
      const res = await api.get("/items/preview-codes", {
        params: { name: form.name, quantity: 1 }
      });
      if (res.data.codes?.[0]) {
        setForm((prev) => ({ ...prev, serial_number: res.data.codes[0] }));
        setFieldErrors((prev) => ({ ...prev, serial_number: "" }));
      }
    } catch {
      setError("Gagal generate kode otomatis.");
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.name || !form.name.trim()) {
      errs.name = "Nama barang wajib diisi.";
    }
    if (!form.quantity || Number(form.quantity) < 1) {
      errs.quantity = "Jumlah barang minimal 1.";
    }
    if (!form.condition) {
      errs.condition = "Kondisi barang wajib dipilih.";
    }
    if (!form.status) {
      errs.status = "Status barang wajib dipilih.";
    }
    if (form.purchase_price && Number(form.purchase_price) < 0) {
      errs.purchase_price = "Harga beli tidak boleh bernilai negatif.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validate()) {
      setError("Mohon lengkapi kolom yang wajib diisi dengan benar.");
      return;
    }

    setLoading(true);

    try {
      const payload = { ...form, quantity: Number(form.quantity) || 1 };

      if (!payload.room_id)        delete payload.room_id;
      if (!payload.purchase_price) delete payload.purchase_price;
      if (!payload.note)           delete payload.note;

      if (payload.purchase_date) {
        payload.purchase_date = payload.purchase_date.replace("T", " ") + ":00";
      } else {
        delete payload.purchase_date;
      }

      let res;
      let msg = "";
      if (isEdit) {
        delete payload.note;
        res = await api.put(`/items/${id}`, payload);
        msg = res.data?.message || "Data barang berhasil diperbarui!";
      } else {
        res = await api.post("/items", payload);
        msg = res.data?.message || "Barang baru berhasil disimpan!";
      }

      setSuccessMsg(msg);

      // Arahkan ke halaman tujuan setelah notifikasi terlihat
      setTimeout(() => {
        const target = isEdit
          ? `/${window.location.pathname.split("/")[1]}/items/${id}`
          : `/${window.location.pathname.split("/")[1]}/items`;
        navigate(target, { state: { message: msg } });
      }, 1000);

    } catch (err) {
      const errs = err.response?.data?.errors;
      if (errs) {
        const mappedErrors = {};
        Object.keys(errs).forEach((k) => {
          mappedErrors[k] = Array.isArray(errs[k]) ? errs[k][0] : errs[k];
        });
        setFieldErrors(mappedErrors);
        setError(Object.values(errs).flat().join(" "));
      } else {
        setError(err.response?.data?.message || "Gagal menyimpan barang.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingInit) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#1677ff" }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Button
          onClick={() => navigate(isEdit ? `/${window.location.pathname.split("/")[1]}/items/${id}` : `/${window.location.pathname.split("/")[1]}/items`)}
          startIcon={<ArrowBackIcon />} size="small"
          sx={{ textTransform: "none", color: "#595959", "&:hover": { backgroundColor: "#f5f5f5" } }}>
          Kembali
        </Button>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "1.25rem", color: "#1a1a2e" }}>
            {isEdit ? "Edit Barang" : "Tambah Barang"}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "#8c8c8c" }}>
            {isEdit ? "Ubah data barang yang sudah ada" : "Isi data barang baru"}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: "1px solid #e6ebf1", borderRadius: "10px", p: 3 }}
        component="form" onSubmit={handleSubmit}>

        {/* Informasi Barang */}
        <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e", mb: 2 }}>
          Informasi Barang
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField fullWidth required label="Nama Barang" name="name"
              value={form.name} onChange={handleChange} size="small" sx={fieldSx}
              error={Boolean(fieldErrors.name)} helperText={fieldErrors.name} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField fullWidth label="Kode / Serial Number" name="serial_number"
              value={form.serial_number} onChange={handleChange} size="small" sx={fieldSx}
              error={Boolean(fieldErrors.serial_number)}
              helperText={
                fieldErrors.serial_number || (
                  !isEdit ? (
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleGenerateCode}
                      disabled={generatingCode}
                      sx={{ p: 0, minWidth: "auto", fontSize: "0.72rem", textTransform: "none" }}
                    >
                      {generatingCode ? "Mengenerate..." : "⚡ Generate Otomatis"}
                    </Button>
                  ) : ""
                )
              }
              InputProps={{ sx: { fontFamily: "monospace" } }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField fullWidth required label="Jumlah" name="quantity"
              type="number" value={form.quantity} onChange={handleChange}
              size="small" sx={fieldSx} inputProps={{ min: 1 }}
              error={Boolean(fieldErrors.quantity)} helperText={fieldErrors.quantity} />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Satuan</InputLabel>
              <Select name="unit" value={form.unit || "Unit"} label="Satuan" onChange={handleChange}>
                {UNIT_OPTIONS.map((u) => (
                  <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Kategori" name="category"
              value={form.category} onChange={handleChange} size="small" sx={fieldSx}
              placeholder="e.g. Elektronik, Furnitur" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small" sx={fieldSx} error={Boolean(fieldErrors.condition)}>
              <InputLabel>Kondisi</InputLabel>
              <Select name="condition" value={form.condition} label="Kondisi" onChange={handleChange}>
                {CONDITIONS.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
              {fieldErrors.condition && (
                <Typography sx={{ color: "#d32f2f", fontSize: "0.72rem", mt: 0.5, ml: 1.5 }}>
                  {fieldErrors.condition}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth label="Spesifikasi" name="specification"
              value={form.specification} onChange={handleChange}
              size="small" multiline rows={2} sx={fieldSx}
              placeholder="e.g. Intel Core i5, RAM 8GB, SSD 512GB" />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth label="Deskripsi / Catatan Tambahan" name="description"
              value={form.description} onChange={handleChange}
              size="small" multiline rows={2} sx={fieldSx} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small" sx={fieldSx} error={Boolean(fieldErrors.status)}>
              <InputLabel>Status</InputLabel>
              <Select name="status" value={form.status} label="Status" onChange={handleChange}>
                {STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
              {fieldErrors.status && (
                <Typography sx={{ color: "#d32f2f", fontSize: "0.72rem", mt: 0.5, ml: 1.5 }}>
                  {fieldErrors.status}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>

        {/* Informasi Pembelian */}
        <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e", mt: 3, mb: 2 }}>
          Informasi Pembelian
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Tanggal & Jam Beli" name="purchase_date"
              type="datetime-local" value={form.purchase_date} onChange={handleChange}
              size="small" sx={fieldSx} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Harga Beli (Rp)" name="purchase_price" type="number"
              value={form.purchase_price} onChange={handleChange} size="small" sx={fieldSx}
              inputProps={{ min: 0 }}
              error={Boolean(fieldErrors.purchase_price)} helperText={fieldErrors.purchase_price} />
          </Grid>
        </Grid>

        {/* Lokasi */}
        <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e", mt: 3, mb: 2 }}>
          {isEdit ? "Ruangan" : "Lokasi Awal"}
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Ruangan</InputLabel>
              <Select name="room_id" value={form.room_id} label="Ruangan" onChange={handleChange}>
                <MenuItem value=""><em>Tidak ada</em></MenuItem>
                {rooms.map((r) => (
                  <MenuItem key={r.id} value={String(r.id)}>{r.name} ({r.code})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {!isEdit && (
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Catatan (opsional)" name="note"
                value={form.note} onChange={handleChange} size="small" sx={fieldSx}
                placeholder="Catatan saat barang pertama masuk" />
            </Grid>
          )}

          {isEdit && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ borderRadius: "8px", fontSize: "0.8rem" }}>
                Untuk pindah ruangan, gunakan tombol <strong>Pindahkan</strong> di halaman detail
                agar riwayat perpindahan tercatat dengan benar.
              </Alert>
            </Grid>
          )}
        </Grid>

        {/* Tombol */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          <Button onClick={() => navigate(isEdit ? `/${window.location.pathname.split("/")[1]}/items/${id}` : `/${window.location.pathname.split("/")[1]}/items`)}
            sx={{ textTransform: "none", color: "#595959" }}>
            Batal
          </Button>
          <Button type="submit" variant="contained" disabled={loading}
            sx={{
              textTransform: "none", borderRadius: "8px",
              backgroundColor: "#1677ff", "&:hover": { backgroundColor: "#0958d9" },
              minWidth: 130,
            }}>
            {loading
              ? <CircularProgress size={18} sx={{ color: "#fff" }} />
              : isEdit ? "Simpan Perubahan" : "Simpan Barang"}
          </Button>
        </Box>
      </Paper>

      {/* Notifikasi Berhasil Disimpan */}
      <Snackbar
        open={Boolean(successMsg)}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessMsg("")}
          sx={{
            width: "100%",
            borderRadius: "8px",
            fontWeight: 600,
            boxShadow: "0 4px 16px rgba(22, 163, 74, 0.3)",
            fontSize: "0.875rem",
          }}
        >
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
